/**
 * Slim Axios client for the Rails admin API. One request interceptor (attach
 * bearer) and one response interceptor (401 → onUnauthorized, except the
 * `/session` login call which owns its own 401). Auth state lives in the store,
 * which wires token/unauthorized callbacks via `configureApiClient`.
 */
import axios, { type AxiosError, type AxiosInstance } from 'axios'
import { env } from './env'

type TokenProvider = () => string | null
type UnauthorizedHandler = () => void

let getToken: TokenProvider = () => null
let onUnauthorized: UnauthorizedHandler = () => {}

export function configureApiClient(opts: {
  getToken: TokenProvider
  onUnauthorized: UnauthorizedHandler
}): void {
  getToken = opts.getToken
  onUnauthorized = opts.onUnauthorized
}

/**
 * Serialize query params to the bracket-array syntax the Rails API expects:
 * `status[]=a&status[]=b`. Scalars are emitted plainly; null/undefined are
 * skipped so optional filters drop out of the URL entirely.
 */
function serializeParams(params: Record<string, unknown>): string {
  const parts: string[] = []
  const push = (key: string, value: unknown) => {
    if (value === undefined || value === null) return
    parts.push(`${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`)
  }
  for (const [key, value] of Object.entries(params)) {
    if (Array.isArray(value)) {
      for (const item of value) {
        if (item === undefined || item === null) continue
        parts.push(`${encodeURIComponent(key)}[]=${encodeURIComponent(String(item))}`)
      }
    } else {
      push(key, value)
    }
  }
  return parts.join('&')
}

export const api: AxiosInstance = axios.create({
  baseURL: env.apiBaseUrl,
  withCredentials: false,
  timeout: 60_000,
  headers: { 'Content-Type': 'application/json' },
  paramsSerializer: { serialize: serializeParams },
})

api.interceptors.request.use((config) => {
  const token = getToken()
  if (token) {
    config.headers.Authorization = `Bearer ${token}`
  }
  return config
})

api.interceptors.response.use(
  (response) => response,
  (error: AxiosError) => {
    const status = error.response?.status
    const url = error.config?.url ?? ''
    // The login call owns its own 401 (bad credentials) — never trigger the
    // global logout/redirect for it.
    if (status === 401 && !url.endsWith('/session')) {
      onUnauthorized()
    }
    throw error
  },
)
